import React, { useState, useEffect } from 'react';
import { Typography, Container, Grid, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button, TextField, Snackbar, Alert } from '@mui/material';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import Loader from '../Loader';
import useCircleHandler from '../hooks/userCircleHandler';
import UserCard from './UserCard';

const Circle = ({ idToken }) => {
  const [confirmationDialogOpened, setConfirmationDialogOpened] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [addUserDialogOpened, setAddUserDialogOpened] = useState(false);
  const [newUserEmail, setNewUserEmail] = useState('');
  const [loading, followedUsers, usersRecommended, followUser, unfollowUser, loadCircle] = useCircleHandler(idToken);
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    loadCircle();
  }, []);

  const handleUnfollowClick = (user) => {
    setSelectedUser(user);
    setConfirmationDialogOpened(true);
  };

  const handleClose = () => {
    setConfirmationDialogOpened(false);
    setAddUserDialogOpened(false);
    setSelectedUser(null);
  };

  const handleConfirmUnfollow = async () => {
    await unfollowUser(selectedUser.email);
    setConfirmationDialogOpened(false);
    setSelectedUser(null);
  };

  const handleAddUserClick = () => {
    setAddUserDialogOpened(true);
  };

  const handleAddUserClose = () => {
    setAddUserDialogOpened(false);
    setNewUserEmail('');
  };

  const handleAddUser = async () => {
    try {
      const result = await followUser(newUserEmail);
      if (result) {
        setNotification({ open: true, message: 'Usuario seguido con éxito.', severity: 'success' });
      } else {
        setNotification({ open: true, message: 'El usuario no existe. Invítalo a registrarse.', severity: 'info' });
      }
    } catch (error) {
      setNotification({ open: true, message: 'Ocurrió un error al intentar seguir al usuario.', severity: 'error' });
    }
    setAddUserDialogOpened(false);
    setNewUserEmail('');
  };

  const handleFollowCritic = async (critic) => {
    try {
      const result = await followUser(critic.email);
      if (result) {
        setNotification({ open: true, message: 'Usuario seguido con éxito.', severity: 'success' });
      } else {
        setNotification({ open: true, message: 'El usuario no existe. Invítalo a registrarse.', severity: 'info' });
      }
    } catch (error) {
      setNotification({ open: true, message: 'Ocurrió un error al intentar seguir al usuario.', severity: 'error' });
    }
    setAddUserDialogOpened(false);
    setNewUserEmail('');
  };

  const handleNotificationClose = () => {
    setNotification({ open: false, message: '', severity: 'success' });
  };

  const unfollowedCritics = (followedUsers && usersRecommended)? usersRecommended.filter(
    (userRecommended) => !followedUsers.some((user) => user.email === userRecommended.email)
  ):[];

  return (
    <>
      {(followedUsers === null || loading) && <Loader />}
      <Container maxWidth="md" sx={{ padding: 2 }}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Typography variant="h5" gutterBottom align='center' sx={{ mt: 4, mb: 2 }}>
              ¿A quién quieres seguir?
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={6}>
            <UserCard 
              name="Seguir usuario" 
              avatarIcon = {<PersonAddIcon/>} 
              avatarColor = "primary.main"
              buttonIcon={<PersonAddIcon />} 
              buttonColor="primary" 
              buttonOnClick={handleAddUserClick} 
              isCritic={false} 
              onCardClick={handleAddUserClick}
            />
          </Grid>
          {unfollowedCritics.map((critic, index) => (
            <Grid item xs={12} sm={6} md={6} key={index}>
              <UserCard 
                name={`${critic.first_name} ${critic.last_name}`} 
                buttonIcon={<PersonAddIcon />} 
                buttonColor="primary" 
                buttonOnClick={() => handleFollowCritic(critic)} 
                extraContent={critic.extra_content}
                isCritic={true}
              />
            </Grid>
          ))}
        </Grid>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Typography variant="h5" gutterBottom align='center' sx={{ mt: 4, mb: 2 }}>
              Tu circulo
            </Typography>
          </Grid>
          {followedUsers?.length===0 && 
            <Grid item xs={12}>
              <Typography variant="body2"  align='center'>
                Aun no tienes amigos. ¡Invita amigos a tu círculo para descubrir que estan recomendando!
              </Typography>
            </Grid>
          }
          {followedUsers && followedUsers.map((user, index) => (
            <Grid item xs={12} sm={6} md={6} key={index}>
              <UserCard 
                name={`${user.first_name} ${user.last_name}`} 
                buttonIcon={<PersonRemoveIcon />} 
                buttonColor="error" 
                buttonOnClick={() => handleUnfollowClick(user)} 
                extraContent={user.email}
                isCritic={user.is_critic}
              />
            </Grid>
          ))}
        </Grid>
      </Container>
      <Dialog open={confirmationDialogOpened} onClose={handleClose}>
        <DialogTitle>Dejar de seguir</DialogTitle>
        <DialogContent>
          <DialogContentText>
            ¿Estás seguro que quieres dejar de seguir a {selectedUser?.first_name} {selectedUser?.last_name}?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="primary" disabled={loading}>
            Cancelar
          </Button>
          <Button onClick={handleConfirmUnfollow} color="error" disabled={loading}>
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={addUserDialogOpened} onClose={handleAddUserClose}>
        <DialogTitle>Agregar usuario</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Ingresa el correo electrónico del usuario que quieres seguir:
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            id="newUserEmail"
            label="Correo electrónico"
            type="email"
            fullWidth
            value={newUserEmail}
            onChange={(e) => setNewUserEmail(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleAddUserClose} color="primary">
            Cancelar
          </Button>
          <Button onClick={handleAddUser} color="primary">
            Agregar
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleNotificationClose}
      >
        <Alert onClose={handleNotificationClose} severity={notification.severity} sx={{ width: '100%' }}>
          {notification.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default Circle;
